// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2)
// version  : 2.2
// updated  : 2026-09-02
// protected: NO
// changelog: v2.2 (Slice Rencana Jual) - tab "Harga Jual Store" diganti daftar SEMPIT
//            (Merchandize|Klub|Modal|Rencana Jual|Harga|Aksi) + filter + panel geser
//            (Sheet): tentukan rencana jual -> centang size -> isi harga. Endpoint TUNGGAL
//            GET/PUT /store-sell-plan. Grid 11 kolom lama dihapus. FRONTEND SAJA.
//            v2.1 (Slice Harga Jual) - gabung tab harga jual + hak akses per sel.
//            v2.0 (Parity P0-2) - tab Master Merchandize (CRUD + cost_price).
// =============================================================================
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Package, Loader2, Save, Lock, AlertTriangle, Plus, Pencil, Search,
  CheckCircle2, XCircle, SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { rupiah, STORES, isOwner } from "@/v2/components/helpers";
import { TableScroll } from "@/v2/components/TableScroll";

const STORE_LABEL = { STORE_AREMA: "Store Arema", STORE_BORNEO: "Store Borneo" };
const MANAGE_ROLES = ["OWNER", "KACAB_SBY", "KACAB_SMD"];
const STAFF_STORE_ROLES = ["STAFF_STORE_AREMA", "STAFF_STORE_BORNEO"];
const BIG_SIZES = ["2XL", "3XL", "4XL", "5XL", "6XL", "7XL"];
// Kolom rencana: 'NORMAL' = S–XL & barang tanpa size, lalu 2XL..7XL.
const PLAN_COLS = ["NORMAL", ...BIG_SIZES];
const COL_LABEL = { NORMAL: "S–XL" };
const colLabel = (c) => COL_LABEL[c] || c;

// =============================== MASTER PRODUK =============================== //
function MasterProductTab() {
  const { api, me } = useV2Auth();
  const canManage = MANAGE_ROLES.includes(me?.role);
  const ownerRole = isOwner(me?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const emptyForm = { id: null, name: "", club: "", season: "2026", cost_price: "", active: true, has_size_variants: true, sku: "" };
  const [form, setForm] = useState(emptyForm);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/api/v2/merchandise/products?include_inactive=${includeInactive}`);
      if (r.ok) setData(await r.json());
      else { const d = await r.json(); toast.error(d.detail || "Gagal memuat Merchandize"); setData(null); }
    } finally { setLoading(false); }
  }, [api, includeInactive]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p) => {
    setForm({ id: p.id, name: p.name, club: p.club, season: p.season || "",
      cost_price: String(p.cost_price ?? ""), active: p.active,
      has_size_variants: p.has_size_variants !== false, sku: p.sku || "" });
    setDialogOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const name = form.name.trim();
    const club = form.club.trim();
    const cost = Number(form.cost_price);
    if (!name || !club) { toast.error("Nama dan klub wajib diisi"); return; }
    if (form.cost_price === "" || isNaN(cost) || cost < 0) { toast.error("Harga modal harus angka >= 0"); return; }
    setSaving(true);
    try {
      if (!form.id) {
        const r = await api(`/api/v2/merchandise/products`, {
          method: "POST",
          body: JSON.stringify({ name, club, season: form.season || null, cost_price: cost, active: form.active, has_size_variants: form.has_size_variants }),
        });
        const d = await r.json();
        if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal menambah Merchandize"); return; }
        toast.success(`Merchandize "${d.name}" ditambahkan`);
      } else {
        const putBody = { name, club, season: form.season || null, active: form.active, has_size_variants: form.has_size_variants };
        if (ownerRole && (form.sku || "").trim()) putBody.sku = form.sku.trim();
        const r1 = await api(`/api/v2/merchandise/products/${form.id}`, {
          method: "PUT",
          body: JSON.stringify(putBody),
        });
        const d1 = await r1.json();
        if (!r1.ok) { toast.error(typeof d1.detail === "string" ? d1.detail : "Gagal menyimpan Merchandize"); return; }
        const r2 = await api(`/api/v2/merchandise/products/${form.id}/cost-price`, {
          method: "PUT", body: JSON.stringify({ cost_price: cost }),
        });
        const d2 = await r2.json();
        if (!r2.ok) { toast.error(typeof d2.detail === "string" ? d2.detail : "Gagal menyimpan harga modal"); return; }
        toast.success(`Merchandize "${name}" diperbarui`);
      }
      setDialogOpen(false);
      await load();
    } finally { setSaving(false); }
  };

  const toggleActive = async (p) => {
    setBusyId(p.id);
    try {
      const r = await api(`/api/v2/merchandise/products/${p.id}/active`, {
        method: "PUT", body: JSON.stringify({ active: !p.active }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal mengubah status"); return; }
      toast.success(`"${p.name}" ${d.active ? "diaktifkan" : "dinonaktifkan"}`);
      await load();
    } finally { setBusyId(null); }
  };

  const rows = data?.items || [];

  return (
    <div className="space-y-4" data-testid="v2-merch-products-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {canManage && (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Switch checked={includeInactive} onCheckedChange={setIncludeInactive}
                data-testid="v2-merch-include-inactive" />
              Tampilkan nonaktif
            </label>
          )}
        </div>
        {canManage && (
          <Button size="sm" onClick={openCreate} data-testid="v2-merch-add-product">
            <Plus className="w-4 h-4 mr-1" /> Tambah Merchandize
          </Button>
        )}
      </div>

      <Card className="p-5 rounded-2xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Master Merchandize</h3>
          {data && <Badge variant="outline" data-testid="v2-merch-product-count">{data.total} Merchandize</Badge>}
        </div>
        <TableScroll>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Merchandize</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Klub</TableHead>
              <TableHead>Region</TableHead>
              <TableHead>Season</TableHead>
              <TableHead className="text-right">Harga Modal</TableHead>
              <TableHead>Status</TableHead>
              {canManage && <TableHead className="w-52 text-right">Aksi</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={canManage ? 8 : 7} className="text-center py-8">
                <Loader2 className="w-5 h-5 animate-spin inline" />
              </TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={canManage ? 8 : 7} className="text-center text-muted-foreground py-8 text-sm">
                Belum ada Merchandize.
              </TableCell></TableRow>
            )}
            {!loading && rows.map((p) => (
              <TableRow key={p.id} data-testid="v2-merch-product-row">
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="font-mono text-xs" data-testid="v2-merch-sku">{p.sku || "-"}</TableCell>
                <TableCell><Badge variant="secondary">{p.club}</Badge></TableCell>
                <TableCell className="text-sm text-muted-foreground">{p.region}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{p.season || "-"}</TableCell>
                <TableCell className="text-right" data-testid="v2-merch-cost">{rupiah(p.cost_price)}</TableCell>
                <TableCell>
                  {p.active
                    ? <Badge variant="success">Aktif</Badge>
                    : <Badge variant="outline" className="text-muted-foreground">Nonaktif</Badge>}
                </TableCell>
                {canManage && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(p)}
                        data-testid="v2-merch-edit-product">
                        <Pencil className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant={p.active ? "ghost" : "secondary"}
                        disabled={busyId === p.id} onClick={() => toggleActive(p)}
                        data-testid="v2-merch-toggle-product">
                        {busyId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (p.active ? "Nonaktifkan" : "Aktifkan")}
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableScroll>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="v2-merch-product-dialog">
          <DialogHeader>
            <DialogTitle>{form.id ? "Edit Merchandize" : "Tambah Merchandize"}</DialogTitle>
            <DialogDescription>
              Region pemilik diturunkan otomatis (klub harus mengandung "Arema" atau "Borneo").
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Merchandize</Label>
              <Input value={form.name} onChange={(e) => setField("name", e.target.value)}
                placeholder="mis. Jersey Home 2026" data-testid="v2-merch-input-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Klub</Label>
                <Input value={form.club} onChange={(e) => setField("club", e.target.value)}
                  placeholder="Arema / Borneo" data-testid="v2-merch-input-club" />
              </div>
              <div>
                <Label className="text-xs">Season</Label>
                <Input value={form.season} onChange={(e) => setField("season", e.target.value)}
                  placeholder="2026" data-testid="v2-merch-input-season" />
              </div>
            </div>
            <div>
              <Label className="text-xs">SKU</Label>
              {!form.id ? (
                <p className="text-xs text-muted-foreground mt-1" data-testid="v2-merch-sku-auto">
                  Dibuat otomatis oleh sistem (pola KLUB-TAHUN-URUT, mis. ARM-2026-001).
                </p>
              ) : ownerRole ? (
                <Input value={form.sku} onChange={(e) => setField("sku", e.target.value)}
                  placeholder="mis. ARM-2026-001" className="font-mono"
                  data-testid="v2-merch-input-sku" />
              ) : (
                <p className="text-sm font-mono mt-1" data-testid="v2-merch-sku-readonly">{form.sku || "-"}</p>
              )}
              {form.id && ownerRole && (
                <p className="text-[11px] text-muted-foreground mt-1">Hanya Owner boleh mengubah SKU; wajib unik.</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Harga Modal (cost_price)</Label>
              <Input type="number" min="0" value={form.cost_price}
                onChange={(e) => setField("cost_price", e.target.value)}
                placeholder="0" data-testid="v2-merch-input-cost" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.active} onCheckedChange={(v) => setField("active", v)}
                data-testid="v2-merch-input-active" />
              Aktif (muncul untuk transaksi)
            </label>
            <div className="rounded-lg border p-3 space-y-2">
              <Label className="text-xs font-medium">Apakah Merchandize ini memiliki pilihan size?</Label>
              <Select value={form.has_size_variants ? "yes" : "no"}
                onValueChange={(v) => setField("has_size_variants", v === "yes")}>
                <SelectTrigger data-testid="v2-merch-input-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes" data-testid="v2-merch-size-yes">Ya, memiliki size (S–7XL)</SelectItem>
                  <SelectItem value="no" data-testid="v2-merch-size-no">Tidak, tanpa size (mis. kaos kaki, rompi)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="v2-merch-product-cancel">Batal</Button>
            <Button onClick={submit} disabled={saving} data-testid="v2-merch-product-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ================= RENCANA JUAL PER-STORE (rencana + harga) ================= //
// Daftar SEMPIT (muat 1366x768 tanpa geser mendatar). Atur -> panel geser (Sheet):
// tentukan rencana jual -> centang size -> isi harga.
// Hak akses per SEL (sama utk rencana & harga; sesuai guard backend):
// Hak akses (SLICE H1): isi & revisi rencana jual + harga = WEWENANG MUTLAK Staff Store
// (store-nya, berkali-kali, tanpa penguncian). Owner & Kepala Cabang: BACA SAJA.
// Owner via Assist Mode berperan sbg Staff Store (me.role EFEKTIF = STAFF_STORE_*) -> boleh.
function SellPlanTab() {
  const { api, me, booting } = useV2Auth();
  const owner = isOwner(me?.role);
  const canWrite = STAFF_STORE_ROLES.includes(me?.role);

  const [store, setStore] = useState(owner ? "STORE_AREMA" : "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  // Panel (Sheet) state
  const [sheetOpen, setSheetOpen] = useState(false);
  const [active, setActive] = useState(null);       // item yang diedit
  const [sellPlan, setSellPlan] = useState("YES");
  const [checked, setChecked] = useState({});       // {col: bool}
  const [priceDrafts, setPriceDrafts] = useState({}); // {col: string}
  const [colStatus, setColStatus] = useState({});   // {col: {ok,msg}}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    // B-1: JANGAN kirim request apa pun sebelum identitas diketahui (render pertama `me`
    // masih null -> owner=false -> dulu qs kosong -> backend 400 -> banner merah). Tunggu.
    if (booting || !me) { setLoading(true); return; }
    // Owner efektif tapi store BELUM dipilih -> tampilkan empty-state, jangan request.
    if (owner && !store) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const qs = owner && store ? `?store=${store}` : "";
      const r = await api(`/api/v2/store-sell-plan${qs}`);
      if (r.ok) { setData(await r.json()); }
      else if (r.status === 401) { setData(null); }  // B-3: sesi habis / belum auth -> diamkan
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Gagal memuat rencana jual"); setData(null); }
    } finally { setLoading(false); }
  }, [api, owner, store, me, booting]);

  // B-2: pulihkan maksud asli — set store default Owner SATU KALI ketika `me` tiba.
  useEffect(() => { if (owner && !store) setStore("STORE_AREMA"); }, [owner]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (q && !it.name.toLowerCase().includes(q)) return false;
      const yes = it.sell_plan === "YES";
      const complete = yes && it.planned_total > 0 && it.filled_count >= it.planned_total;
      switch (filter) {
        case "undetermined": return !it.plan_exists;
        case "incomplete": return yes && it.filled_count < it.planned_total;
        case "complete": return complete;
        case "notsold": return it.sell_plan === "NO";
        case "stock_no_price": return it.stock_no_price === true;  // A-5: ada stok, belum ada harga
        default: return true;
      }
    });
  }, [items, filter, query]);

  // ---- panel helpers ----
  const priceCellOf = (item, col) => (col === "NORMAL"
    ? item.normal
    : (item.size_prices?.[col] || { has_price: false, price: null, price_id: null }));

  const openPanel = (item) => {
    setActive(item);
    setSellPlan(item.plan_exists ? item.sell_plan : "YES");
    const chk = {};
    const drafts = {};
    const cols = item.has_size_variants ? PLAN_COLS : ["NORMAL"];
    const initialChecked = item.plan_exists ? new Set(item.sizes) : new Set(item.has_size_variants ? ["NORMAL"] : ["NORMAL"]);
    for (const c of cols) {
      chk[c] = initialChecked.has(c);
      const cell = priceCellOf(item, c);
      drafts[c] = cell.has_price ? String(cell.price) : "";
    }
    setChecked(chk);
    setPriceDrafts(drafts);
    setColStatus({});
    setSheetOpen(true);
  };

  const canEditPlan = canWrite;                 // Staff Store: selalu boleh (isi & revisi, tanpa kunci)
  const priceEditable = () => canWrite;         // harga selalu bisa diisi/diubah oleh Staff Store

  const setDraft = (col, val) => {
    setPriceDrafts((d) => ({ ...d, [col]: val }));
    setColStatus((s) => { if (!s[col]) return s; const n = { ...s }; delete n[col]; return n; });
  };

  const save = async () => {
    if (!active) return;
    const item = active;
    const storeName = data?.store;
    const cols = item.has_size_variants ? PLAN_COLS : ["NORMAL"];
    setSaving(true);
    const status = { ...colStatus };
    let okCount = 0, errCount = 0;

    // 1) Rencana jual (PUT /store-sell-plan) bila berubah & user berhak.
    const newSizes = sellPlan === "YES" ? cols.filter((c) => checked[c]) : [];
    const curSizes = new Set(item.plan_exists ? item.sizes : []);
    const planChanged = !item.plan_exists
      || item.sell_plan !== sellPlan
      || newSizes.length !== curSizes.size
      || newSizes.some((s) => !curSizes.has(s));
    if (planChanged) {
      if (!canEditPlan) {
        status["_plan"] = { ok: false, msg: "Hanya Staff Store yang dapat mengubah rencana jual." };
        errCount++;
      } else {
        try {
          const r = await api(`/api/v2/store-sell-plan`, {
            method: "PUT",
            body: JSON.stringify({ product_id: item.product_id, store: storeName, sell_plan: sellPlan, sizes: newSizes }),
          });
          const d = await r.json().catch(() => ({}));
          if (!r.ok) { status["_plan"] = { ok: false, msg: (typeof d.detail === "string" ? d.detail : "Gagal") }; errCount++; }
          else { status["_plan"] = { ok: true, msg: "Rencana tersimpan" }; okCount++; }
        } catch (e) { status["_plan"] = { ok: false, msg: "Kesalahan jaringan" }; errCount++; }
      }
    }

    // 2) Harga per size yang dicentang (hanya bila sell_plan=YES), untuk yang berubah.
    //    SLICE H1: selalu POST (upsert) — backend menerima isi & revisi berkali-kali, tolak < modal.
    if (sellPlan === "YES") {
      for (const col of cols) {
        if (!checked[col]) continue;
        const raw = priceDrafts[col];
        const cell = priceCellOf(item, col);
        const baseline = cell.has_price ? String(cell.price) : "";
        if (raw === undefined || raw === "" || raw === baseline) continue; // tak berubah
        if (!priceEditable(cell)) {
          status[col] = { ok: false, msg: "Hanya Staff Store yang dapat mengisi harga." };
          errCount++; continue;
        }
        const value = Number(raw);
        if (isNaN(value) || value <= 0) { status[col] = { ok: false, msg: "Harga harus > 0" }; errCount++; continue; }
        try {
          const r = col === "NORMAL"
            ? await api(`/api/v2/store-prices`, { method: "POST", body: JSON.stringify({ product_id: item.product_id, store: storeName, price: value }) })
            : await api(`/api/v2/store-size-prices`, { method: "POST", body: JSON.stringify({ product_id: item.product_id, store: storeName, size: col, price: value }) });
          const d = await r.json().catch(() => ({}));
          if (!r.ok) { status[col] = { ok: false, msg: (typeof d.detail === "string" ? d.detail : "Gagal") }; errCount++; }
          else { status[col] = { ok: true, msg: "Tersimpan" }; okCount++; }
        } catch (e) { status[col] = { ok: false, msg: "Kesalahan jaringan" }; errCount++; }
      }
    }

    setColStatus(status);
    setSaving(false);
    if (okCount && !errCount) { toast.success(`${item.name}: tersimpan`); }
    else if (okCount && errCount) { toast.warning(`${item.name}: sebagian tersimpan, ${errCount} gagal`); }
    else if (!okCount && !errCount) { toast.info("Tidak ada perubahan"); }
    else { toast.error(`${item.name}: gagal (${errCount})`); }
    await load();
    // refresh panel dari data terbaru (biar counter & harga akur)
    if (errCount === 0) setSheetOpen(false);
  };

  // S-A4 (A4-3): rem di depan — "Ya, dijual" wajib >=1 size dicentang. Produk tanpa varian
  // size selalu terwakili "NORMAL" (tak pernah dianggap kosong). Jangan andalkan toast backend saja.
  const activeCols = active ? (active.has_size_variants ? PLAN_COLS : ["NORMAL"]) : [];
  const anySizeChecked = activeCols.some((c) => checked[c]);
  const planIncomplete = sellPlan === "YES" && !anySizeChecked;

  // ---- render list helpers ----
  const planChip = (it) => {
    if (!it.plan_exists) return <Badge variant="outline" className="text-muted-foreground" data-testid="v2-merch-plan-chip">Belum ditentukan</Badge>;
    if (it.sell_plan === "NO") return <Badge variant="danger" data-testid="v2-merch-plan-chip">Tidak dijual di store</Badge>;
    return <Badge variant="success" data-testid="v2-merch-plan-chip">Dijual · {it.planned_total} size</Badge>;
  };
  const priceCounter = (it) => {
    if (!it.plan_exists || it.sell_plan === "NO") return <span className="text-muted-foreground text-xs" data-testid="v2-merch-price-counter">—</span>;
    const full = it.planned_total > 0 && it.filled_count >= it.planned_total;
    return <span className={`text-xs tabular-nums ${full ? "text-[hsl(var(--badge-success))]" : "text-[hsl(var(--badge-warning))]"}`} data-testid="v2-merch-price-counter">{it.filled_count}/{it.planned_total} terisi</span>;
  };

  const FILTERS = [
    ["all", "Semua"], ["undetermined", "Perlu ditentukan"], ["incomplete", "Belum lengkap"],
    ["complete", "Sudah lengkap"], ["notsold", "Tidak dijual"],
    ["stock_no_price", "Ada stok, belum ada harga"],
  ];

  return (
    <div className="space-y-4" data-testid="v2-merch-storeprice-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-3xl">
          Tentukan <b>rencana jual</b> tiap merchandize di store ini → centang size → isi harga.
          "Tidak dijual di store" hanya menyembunyikan dari kasir; barang <b>tetap</b> bisa masuk Work Order, dapat QR, dan muncul di laporan stok.
        </p>
        {owner && (
          <div className="w-56">
            <Label className="text-xs">Pilih Store</Label>
            <Select value={store} onValueChange={setStore}>
              <SelectTrigger data-testid="v2-merch-store-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STORES.map((s) => <SelectItem key={s} value={s}>{STORE_LABEL[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Banner DUA angka */}
      {data && (data.undetermined_count > 0 || data.missing_price_count > 0) && (
        <div className="space-y-1" data-testid="v2-merch-missing-banner">
          {data.undetermined_count > 0 && (
            <div className="flex items-center gap-2 text-sm rounded-xl border border-[hsl(var(--badge-info))]/40 bg-[hsl(var(--badge-info))]/10 text-[hsl(var(--badge-info))] px-4 py-2">
              <AlertTriangle className="w-4 h-4" />
              <span><b data-testid="v2-merch-banner-undetermined">{data.undetermined_count}</b> merchandize belum ditentukan rencana jualnya</span>
            </div>
          )}
          {data.missing_price_count > 0 && (
            <button
              type="button"
              onClick={() => data.stock_no_price_count > 0 && setFilter("stock_no_price")}
              className={"flex w-full items-center gap-2 text-left text-sm rounded-xl border border-[hsl(var(--badge-warning))]/40 bg-[hsl(var(--badge-warning))]/10 text-[hsl(var(--badge-warning))] px-4 py-2"
                + (data.stock_no_price_count > 0 ? " hover:bg-[hsl(var(--badge-warning))]/20 cursor-pointer" : " cursor-default")}
              data-testid="v2-merch-banner-missing-btn"
            >
              <AlertTriangle className="w-4 h-4" />
              <span>
                <b data-testid="v2-merch-banner-missing">{data.missing_price_count}</b> harga belum diisi untuk merchandize yang direncanakan dijual
                {data.stock_no_price_count > 0 && (
                  <> — <b data-testid="v2-merch-banner-stock-no-price">{data.stock_no_price_count}</b> di antaranya barangnya sudah ada di store</>
                )}
              </span>
            </button>
          )}
        </div>
      )}

      {/* Filter + cari */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([k, lbl]) => (
          <Button key={k} size="sm" variant={filter === k ? "default" : "outline"}
            onClick={() => setFilter(k)} data-testid={`v2-merch-filter-${k}`}>{lbl}</Button>
        ))}
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-8 w-56 h-9" placeholder="Cari merchandize…"
            value={query} onChange={(e) => setQuery(e.target.value)} data-testid="v2-merch-search" />
        </div>
      </div>

      <Card className="p-5 rounded-2xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">{STORE_LABEL[data?.store] || "Store"}</h3>
          {data && <Badge variant="outline" data-testid="v2-merch-count">{filtered.length} / {items.length}</Badge>}
        </div>

        {owner && !store ? (
          <div className="py-8 text-center text-muted-foreground text-sm" data-testid="v2-merch-pick-store">
            Pilih store dulu untuk melihat daftar.
          </div>
        ) : loading ? (
          <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm">Tidak ada merchandize sesuai filter.</div>
        ) : (
          <>
            {/* DESKTOP: tabel sempit */}
            <div className="hidden md:block">
              <TableScroll>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[200px]">Merchandize</TableHead>
                      <TableHead>Klub</TableHead>
                      <TableHead className="text-right">Modal</TableHead>
                      <TableHead>Rencana Jual</TableHead>
                      <TableHead>Harga</TableHead>
                      <TableHead className="text-right">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((it) => (
                      <TableRow key={it.product_id} data-testid="v2-merch-row">
                        <TableCell className="font-medium">{it.name}</TableCell>
                        <TableCell><Badge variant="secondary">{it.club}</Badge></TableCell>
                        <TableCell className="text-right text-muted-foreground" data-testid="v2-merch-cost-ro">{rupiah(it.cost_price)}</TableCell>
                        <TableCell>{planChip(it)}</TableCell>
                        <TableCell>{priceCounter(it)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => openPanel(it)} data-testid="v2-merch-atur-btn">
                            <SlidersHorizontal className="w-4 h-4 mr-1" /> Atur
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableScroll>
            </div>

            {/* MOBILE: kartu ringkas */}
            <div className="md:hidden space-y-2">
              {filtered.map((it) => (
                <div key={it.product_id} className="rounded-xl border p-3 flex items-center gap-2" data-testid="v2-merch-row-mobile">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{it.name}</div>
                    <div className="flex items-center gap-2 mt-1">{planChip(it)}{priceCounter(it)}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => openPanel(it)} data-testid="v2-merch-atur-btn">
                    <SlidersHorizontal className="w-4 h-4 mr-1" /> Atur
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* PANEL GESER (Sheet) */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="v2-merch-plan-sheet">
          {active && (
            <>
              <SheetHeader>
                <SheetTitle>{active.name}</SheetTitle>
                <SheetDescription>{active.club} · Harga modal <b>{rupiah(active.cost_price)}</b> (hanya tampilan)</SheetDescription>
              </SheetHeader>

              {/* B-3: Owner & Kepala Cabang (tanpa Assist) -> BACA SAJA (satu kalimat). */}
              {!canWrite && (
                <div className="mt-4 rounded-lg border bg-muted/40 px-3 py-2 text-sm text-muted-foreground inline-flex items-center gap-2" data-testid="v2-merch-panel-note">
                  <Lock className="w-4 h-4" /> Pengisian harga jual &amp; penentuan dijual/tidak adalah wewenang Staff Store. Anda hanya dapat melihat.
                </div>
              )}

              <div className="mt-5 space-y-5">
                {/* Ya / Tidak */}
                <div>
                  <Label className="text-sm font-medium">Dijual di {STORE_LABEL[data?.store]}?</Label>
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant={sellPlan === "YES" ? "default" : "outline"} disabled={!canEditPlan}
                      onClick={() => setSellPlan("YES")} data-testid="v2-merch-plan-yes">Ya, dijual</Button>
                    <Button size="sm" variant={sellPlan === "NO" ? "default" : "outline"} disabled={!canEditPlan}
                      onClick={() => setSellPlan("NO")} data-testid="v2-merch-plan-no">Tidak dijual di store</Button>
                  </div>
                </div>

                {/* Size + harga (hanya bila YES) */}
                {sellPlan === "YES" && (
                  active.has_size_variants ? (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Size yang dijual &amp; harganya</Label>
                      {PLAN_COLS.map((col) => {
                        const cell = priceCellOf(active, col);
                        const on = !!checked[col];
                        const pEdit = priceEditable(cell);
                        const st = colStatus[col];
                        return (
                          <div key={col} className="rounded-lg border p-2" data-testid={`v2-merch-plan-row-${col}`}>
                            <label className="flex items-center gap-2 text-sm">
                              <Checkbox checked={on} disabled={!canEditPlan}
                                onCheckedChange={(v) => setChecked((c) => ({ ...c, [col]: !!v }))}
                                data-testid={`v2-merch-plan-size-${col}`} />
                              <span className="font-medium">{colLabel(col)}</span>
                              {cell.has_price && <span className="text-xs text-muted-foreground ml-auto">kini {rupiah(cell.price)}</span>}
                            </label>
                            {on && (
                              <div className="mt-2 pl-6">
                                {pEdit ? (
                                  <Input type="number" min="0" className="h-8 w-40 text-xs"
                                    placeholder={cell.has_price ? "ubah harga" : "isi harga jual"}
                                    value={priceDrafts[col] ?? ""} onChange={(e) => setDraft(col, e.target.value)}
                                    data-testid={`v2-merch-plan-price-${col}`} />
                                ) : (
                                  <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                                    <Lock className="w-3 h-3" />Hanya Staff Store yang mengisi harga jual.
                                  </span>
                                )}
                                {st && (
                                  <span className={`ml-2 inline-flex items-center gap-1 text-[10px] ${st.ok ? "text-[hsl(var(--badge-success))]" : "text-destructive"}`} data-testid={`v2-merch-plan-status-${col}`}>
                                    {st.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}{st.ok ? "Tersimpan" : st.msg}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    // Barang tanpa size: satu kotak harga
                    <div className="rounded-lg border p-2" data-testid="v2-merch-plan-row-NORMAL">
                      <Label className="text-sm font-medium">Harga jual</Label>
                      {(() => {
                        const cell = priceCellOf(active, "NORMAL");
                        const pEdit = priceEditable(cell);
                        const st = colStatus["NORMAL"];
                        return (
                          <div className="mt-2">
                            {pEdit ? (
                              <Input type="number" min="0" className="h-8 w-40 text-xs"
                                placeholder={cell.has_price ? "ubah harga" : "isi harga jual"}
                                value={priceDrafts["NORMAL"] ?? ""} onChange={(e) => setDraft("NORMAL", e.target.value)}
                                data-testid="v2-merch-plan-price-NORMAL" />
                            ) : (
                              <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                                <Lock className="w-3 h-3" />Hanya Staff Store yang mengisi harga jual.
                              </span>
                            )}
                            {st && (
                              <span className={`ml-2 inline-flex items-center gap-1 text-[10px] ${st.ok ? "text-[hsl(var(--badge-success))]" : "text-destructive"}`} data-testid="v2-merch-plan-status-NORMAL">
                                {st.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}{st.ok ? "Tersimpan" : st.msg}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )
                )}

                {colStatus["_plan"] && (
                  <div className={`text-xs inline-flex items-center gap-1 ${colStatus["_plan"].ok ? "text-[hsl(var(--badge-success))]" : "text-destructive"}`} data-testid="v2-merch-plan-status">
                    {colStatus["_plan"].ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}{colStatus["_plan"].msg}
                  </div>
                )}

                {canWrite && planIncomplete && (
                  <p className="text-[11px] text-[hsl(var(--badge-warning))] inline-flex items-center gap-1" data-testid="v2-merch-plan-need-size">
                    <AlertTriangle className="w-3 h-3" /> Pilih minimal satu size untuk "dijual", atau pilih "Tidak dijual".
                  </p>
                )}

                {canWrite && (
                  <Button className="w-full" onClick={save} disabled={saving || planIncomplete} data-testid="v2-merch-plan-save">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Simpan
                  </Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// =========================== PAJAK (PPN) per store — Slice 3 ==================== //
function TaxSettingTab() {
  const { api, me, booting } = useV2Auth();
  const owner = isOwner(me?.role);
  const isStaffStore = ["STAFF_STORE_AREMA", "STAFF_STORE_BORNEO"].includes(me?.role);
  const [store, setStore] = useState(owner ? "STORE_AREMA" : "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [percent, setPercent] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    // B-1: tunggu identitas sebelum request apa pun (hindari 400 + banner merah render pertama).
    if (booting || !me) { setLoading(true); return; }
    // Owner efektif tapi store BELUM dipilih -> empty-state, jangan request.
    if (owner && !store) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const qs = owner && store ? `?store=${store}` : "";
      const r = await api(`/api/v2/store-tax-settings${qs}`);
      if (r.ok) {
        const d = await r.json();
        setData(d); setEnabled(!!d.enabled); setPercent(d.percent ? String(d.percent) : "");
      } else if (r.status === 401) { setData(null); }  // B-3: sesi habis / belum auth -> diamkan
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Gagal memuat pengaturan pajak"); setData(null); }
    } finally { setLoading(false); }
  }, [api, owner, store, me, booting]);

  // B-2: set store default Owner SATU KALI ketika `me` tiba.
  useEffect(() => { if (owner && !store) setStore("STORE_AREMA"); }, [owner]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const pct = Number(percent);
    if (enabled && (!percent || isNaN(pct) || pct <= 0)) {
      toast.error("Isi besaran PPN (> 0) untuk mengaktifkan pajak"); return;
    }
    setSaving(true);
    try {
      const r = await api(`/api/v2/store-tax-settings`, {
        method: "PUT", body: JSON.stringify({ enabled, percent: enabled ? pct : 0 }) });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal menyimpan pengaturan pajak"); return; }
      toast.success(enabled ? `PPN aktif · ${pct}%` : "PPN dinonaktifkan");
      await load();
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="v2-merch-tax-tab">
      <div className="rounded-xl border border-[hsl(var(--badge-info))]/40 bg-[hsl(var(--badge-info))]/10 px-4 py-3 text-sm text-[hsl(var(--badge-info))]">
        PPN <b>opsional</b> dan <b>default nonaktif</b>. Hanya <b>Staff Store</b> yang mengaktifkan &amp; mengatur besarannya.
        PPN dihitung <b>setelah diskon</b>, disimpan (snapshot) pada tiap nota, dan merupakan urusan store↔customer —
        <b> tidak memengaruhi penagihan ETAMS ke klub</b>. Mengubah besaran nanti <b>tidak</b> mengubah nota lama.
      </div>

      {owner && (
        <div className="w-56">
          <Label className="text-xs">Pilih Store</Label>
          <Select value={store} onValueChange={setStore}>
            <SelectTrigger data-testid="v2-merch-tax-store-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STORES.map((s) => <SelectItem key={s} value={s}>{STORE_LABEL[s]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <Card className="p-5 rounded-2xl max-w-lg">
        {owner && !store ? (
          <div className="py-6 text-center text-muted-foreground text-sm" data-testid="v2-merch-tax-pick-store">
            Pilih store dulu untuk melihat pengaturan.
          </div>
        ) : loading ? (
          <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">{STORE_LABEL[data?.store] || "Store"}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Status saat ini:{" "}
                  {data?.enabled
                    ? <Badge variant="success" data-testid="v2-merch-tax-status">PPN {data.percent}% aktif</Badge>
                    : <Badge variant="outline" className="text-muted-foreground" data-testid="v2-merch-tax-status">Nonaktif</Badge>}
                </p>
              </div>
            </div>

            {isStaffStore ? (
              <>
                <label className="flex items-center gap-3 text-sm">
                  <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="v2-merch-tax-enable" />
                  Aktifkan PPN untuk store ini
                </label>
                <div>
                  <Label className="text-xs">Besaran PPN (%)</Label>
                  <Input type="number" min="0" max="100" step="0.01" className="mt-1 w-40"
                    placeholder="mis. 11" value={percent} disabled={!enabled}
                    onChange={(e) => setPercent(e.target.value)}
                    data-testid="v2-merch-tax-percent" />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Wajib diisi (&gt; 0) bila PPN diaktifkan. Contoh 11 untuk PPN 11%.
                  </p>
                </div>
                <Button onClick={save} disabled={saving} data-testid="v2-merch-tax-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Simpan Pengaturan
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground inline-flex items-center gap-1" data-testid="v2-merch-tax-readonly">
                <Lock className="w-3.5 h-3.5" /> Hanya Staff Store yang dapat mengubah pengaturan PPN.
              </p>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

export default function MerchandisePage() {
  const { me } = useV2Auth();
  const canManage = MANAGE_ROLES.includes(me?.role);
  const [tab, setTab] = useState("produk");

  return (
    <div className="space-y-5" data-testid="v2-merchandise-page">
      <div>
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <Package className="w-6 h-6 text-primary" /> Merchandize
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Master Merchandize &amp; harga modal {canManage ? "(dikelola Kepala Cabang/Owner per region)" : "(lihat)"} · rencana &amp; harga jual per-store.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="v2-merch-tabs">
          <TabsTrigger value="produk" data-testid="v2-merch-tab-produk">Master Merchandize</TabsTrigger>
          <TabsTrigger value="harga" data-testid="v2-merch-tab-harga">Harga Jual Store</TabsTrigger>
          <TabsTrigger value="pajak" data-testid="v2-merch-tab-pajak">Pajak (PPN)</TabsTrigger>
        </TabsList>
        <TabsContent value="produk" className="mt-4"><MasterProductTab /></TabsContent>
        <TabsContent value="harga" className="mt-4"><SellPlanTab /></TabsContent>
        <TabsContent value="pajak" className="mt-4"><TaxSettingTab /></TabsContent>
      </Tabs>
    </div>
  );
}
